<template>
  <div
    class="container mx-auto flex flex-col items-center font-Almarai min-h-screen relative overflow-x-hidden"
  >
    <LogoSvg class="absolute" />

    <div class="flex justify-center items-center w-3/12 h-36 flex-shrink-0">
      <img src="@/assets/logo-1.webp" alt="logo" class="w-28" />
    </div>

    <!-- Main Content with Flex Layout -->
    <div
      class="flex flex-col w-full max-w-xl rounded-md md:mx-auto mx-2 flex-1 min-h-0 md:max-h-[80vh]"
    >
      <!-- Header Section -->
      <div class="flex flex-col items-center gap-y-4 p-6 pb-4 flex-shrink-0">
        <h1 class="text-gray-600 text-md font-Almarai">خطوة 2 من 2</h1>
        <h1 class="md:text-2xl font-bold text-white font-Almarai">
          اختر طريقة الدفع
        </h1>
        
       
      </div>

      <!-- Scrollable Content Area -->
      <div class="flex-1 overflow-y-auto px-6 min-h-0 max-h-96">
        <!-- Operators Grid -->
        <div v-if="availableOperators.length > 0" class="grid grid-cols-2 gap-4 mt-6 mb-4" dir="rtl">
          <div
            v-for="operator in availableOperators"
            :key="operator"
            class="form-group rounded-md p-4 relative bg-[#18181b] flex flex-col items-center justify-center cursor-pointer transition-all duration-200"
            :class="
              selectedProvider === operator
                ? 'border-2 border-[#9d2890] shadow-[0_0_0_2px_#9d2890]'
                : 'border border-gray-600 hover:border-gray-500'
            "
            @click="selectOperator(operator)"
          >
            <!-- Selection Icon -->
            <span
              v-if="selectedProvider === operator"
              class="absolute top-2 right-2"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="11" stroke="#9d2890" stroke-width="2" />
                <path
                  d="M8 12.5L11 15.5L16 10.5"
                  stroke="#9d2890"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>

            <!-- Operator Logo -->
            <div class="flex flex-col items-center">
              <div class="flex flex-wrap items-center justify-center gap-1 mb-2">
                <img
                  v-for="(logo, logoIndex) in getOperatorLogos(operator)"
                  :key="`${operator}-${logoIndex}`"
                  :src="logo"
                  :alt="`${operator}-${logoIndex}`"
                  class="w-8 h-8 object-contain"
                  :class="{ 'w-16 h-16': getOperatorLogos(operator).length === 1 }"
                />
              </div>
              <span class="text-white text-sm text-center">{{ getOperatorName(operator) }}</span>
            </div>
          </div>
        </div>

        <!-- No operators available message -->
        <div v-else class="text-center text-gray-400 py-8">
          <p>لا توجد طرق دفع متاحة لهذه الباقة</p>
        </div>
      </div>

      <!-- Fixed Footer with Buttons -->
      <div class="flex flex-col items-center gap-y-2 justify-center w-full p-6 pt-4 flex-shrink-0 border-t border-gray-50/10">
        <button
          @click.prevent="redirectUserToProvider"
          :disabled="!selectedProvider"
          :class="{
            'bg-[#9d2890] text-gray-100': selectedProvider,
            'bg-gray-600 text-gray-400 cursor-not-allowed': !selectedProvider
          }"
          class="inline-flex justify-center items-center gap-2 w-full rounded-md py-3 transition-colors"
        >
          <span>متابعة</span>
        </button>
        <button
          @click.prevent="$router.go(-1)"
          class="inline-flex justify-center items-center gap-2 w-full border border-basic text-gray-100 rounded-md py-3"
        >
          <span>رجوع</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import animationData from "@/assets/loader.json";
import LogoSvg from "@/components/svg/LogoSvg.vue";
import { mapActions, mapState } from "pinia";
import { useBravoStore } from "@/stores/Bravo";

import { 
  COUNTRY_PLANS, 
  COUNTRY_CURRENCIES, 
  OPERATOR_LOGOS, 
  landingPageCountries,
   
} from "@/constants";
import { useFawryStore } from "@/stores/fawry";

export default {
  components: {
    LogoSvg,
  },
  data() {
    return {
      toast: useToast(),
      loaded: false,
      lottieOptions: {
        animationData,
        loop: true,
        autoplay: true,
      },
      jsonFile: animationData,
      selectedProvider: null,
      ae_ksa:[],
    };
  },

  computed: {
    ...mapState(useBravoStore, ["getPlans","getRedirectUrl"]),
    ...mapState(useFawryStore, ["getFawryPlans"]),
    country() {
      return this.$route.params.country;
    },
    period() {
      return this.$route.params.period;
    },
    countryPlans() {
      // Map English country names to Arabic for COUNTRY_PLANS
      const countryMap = {
        'sudan': 'السودان',
        'egypt': 'مصر', 
        'saudi': 'السعودية',
        'uae': 'الإمارات',
        'restOfWorld': 'باقي دول العالم'
      };
      return COUNTRY_PLANS[countryMap[this.country]] || [];
    },
    selectedPlan() {
     
      // Find the plan that matches the period
      const plan = this.countryPlans.find(plan => 
        plan.period === this.period || 
        (this.period === 'monthly' && plan.period === 'month') ||
        (this.period === 'yearly' && plan.period === 'year') ||
        (this.period === 'daily' && plan.period === 'day')
      );
      
      return plan;
    },
    countryCurrency() {
      const countryMap = {
        'sudan': 'السودان',
        'egypt': 'مصر', 
        'saudi': 'السعودية',
        'uae': 'الإمارات',
        'restOfWorld': 'باقي دول العالم'
      };
      return COUNTRY_CURRENCIES[countryMap[this.country]] || 'جنيه';
    },
    availableOperators() {
      if (!this.selectedPlan) return [];
      return this.selectedPlan.operators || [];
    },
    landingPageUrls() {
      // Map URL periods to constants periods
      const periodMap = {
        'monthly': 'month',
        'yearly': 'year', 
        'daily': 'day',
        'weekly': 'weekly'
      };
      
      const mappedPeriod = periodMap[this.period] || this.period;
      
      
      return landingPageCountries[this.country] && landingPageCountries[this.country][mappedPeriod] 
        ? landingPageCountries[this.country][mappedPeriod] 
        : {};
    }
  },

  methods: {
    ...mapActions(useBravoStore, ["fetchPlans"]),
    ...mapActions(useFawryStore, ["fetchFawryPlans",'getFawryPaymentUrl']),
    
    async loadPlans() {
      try {
        await this.fetchPlans();
        await this.fetchFawryPlans();
      } catch (error) {
        console.error("Error fetching Bravo plans:", error);
      }
    },

    selectOperator(operator) {
      this.selectedProvider = operator;
    },

    getOperatorLogos(op) {
      // Return array of logos for operators that have multiple logos
      if (op === 'stripe' && Array.isArray(OPERATOR_LOGOS[op])) {
        return OPERATOR_LOGOS[op];
      }
      // Return single logo as array for consistency
      return [OPERATOR_LOGOS[op] || ""];
    },

    getOperatorName(operator) {
      const names = {
        sudani: "سوداني",
        mtn: "أم تي إن", 
        zain: "زين",
        bravo: "برافو",
        mobily: "موبايلي",
        stc: "STC",
        etisalat: "إتصالات",
        du: "دو",
        stripe: "Stripe"
      };
      return names[operator] || operator;
    },

    getUserIdFromUserData() {
      try {
        const userData = localStorage.getItem('user-data');
        if (userData) {
          const user = JSON.parse(userData);
          return user.id || user.user_id || user.phone || user.email || null;
        }
        return null;
      } catch (error) {
        console.error("Error parsing user data:", error);
        return null;
      }
    },

    redirectUserToProvider() {
                  const userId = localStorage.getItem('user_id_sub');

      if (!this.selectedProvider) {
        this.toast.warning("يرجى اختيار طريقة الدفع");
        return;
      }

      
      // Handle Bravo provider with plan matching logic
      if (this.selectedProvider === 'bravo' && this.selectedPlan) {
        this.handleBravoRedirect();
        return;
      }
      // Handle fawry provider with plan matching logic
      if (this.selectedProvider === 'fawry' && this.selectedPlan) {

        this.handleFawryRedirect();
        return;
      }

      const redirectUrl = this.landingPageUrls[this.selectedProvider];
      if (!redirectUrl) {
        this.toast.error("لا يمكن العثور على رابط الدفع");
        return;
      }

      // Handle internal routes (bravo, stripe, auth routes)
      if (redirectUrl.includes('bravo.pay') || 
          redirectUrl.includes('auth.') || 
          redirectUrl === 'auth.subscriptions') {
        
        
        if (redirectUrl === 'bravo.pay') {
          this.$router.push({ name: 'bravo.pay' });
        } else if (redirectUrl === 'auth.subscriptions') {
          this.$router.push({ name: 'auth.subscriptions' });
        } else {
          this.$router.push({ name: redirectUrl });
        }
      } 
      // Handle external URLs
      else {
        const operatorsWithSpecialHandling = ['mobily', 'stc', 'etisalat','du'];
        const origin = localStorage.getItem('origin') || 'web';
        if(operatorsWithSpecialHandling.includes(this.selectedProvider)){
            
            // If userId is not found, redirect without it
            if (!userId) {
               window.location.href = `${redirectUrl}?origin=${origin}`;
               return;
            }
            
            const finalUrl = `${redirectUrl}?referenceUserId=${userId}&origin=${origin}`;
            
            window.location.href = finalUrl;
        } else {
          window.location.href = userId ? `${redirectUrl}?origin=${origin}&user_id=${userId}`: `${redirectUrl}?origin=${origin}`; // Append user ID and origin for tracking
        }
      }
    },
    // Handle Fawry redirection
    handleFawryRedirect() {

      // Find matching plan from the fetched Fawry plans array
      const matchingPlan = this.getFawryPlans.find(fetchedPlan => {
        // Handle different period formats and mappings     
        if (this.selectedPlan.period === "weekly" && fetchedPlan.plan_uuid === "fawrypay-weekly-plan") {
          return true;
        } else if ((this.selectedPlan.period === "month" || this.selectedPlan.period === "monthly") && fetchedPlan.plan_uuid === "fawrypay-monthly-plan") {
          return true;
        } else if ((this.selectedPlan.period === "year" || this.selectedPlan.period === "yearly") && fetchedPlan.plan_uuid === "fawrypay-yearly-plan") {
          return true;
        }
        return false;
      });

      if (matchingPlan) {
       this.getFawryPaymentUrl(localStorage.getItem('user_id_sub'),matchingPlan.id).then(response => {
        console.log("Fawry Payment URL Response:", response);
          if (response && response.redirectURL) {
            // Save plan details to localStorage
            localStorage.setItem('selectedPlan', JSON.stringify({
              id: matchingPlan.id,
              name: matchingPlan.name,
              price: matchingPlan.price,
              period: this.selectedPlan.period
            }));
            
            console.log("Redirecting to Fawry URL:", response.redirectURL);
            // Redirect to Fawry payment URL - use the same property name as the check
            window.location.href = response.redirectURL;
          } else {
            console.error("No redirectURL in response:", response);
            this.toast.error("فشل في الحصول على رابط الدفع من فوري");
          }
        }).catch(error => {
          console.error("Error getting Fawry payment URL:", error);
          this.toast.error("فشل في الاتصال بخدمة فوري");
        });

      }//

    },
    /**
     * Handle Bravo redirection.
     * Finds the matching plan and redirects to the Bravo payment page.
     * Saves the selected plan details to localStorage.
     * 
     */
   async handleBravoRedirect() {
     
      // Find matching plan from the fetched Bravo plans array
      const matchingPlan = this.getPlans.find(fetchedPlan => {
        
        // Handle different period formats and mappings
        if (this.selectedPlan.period === "weekly" && fetchedPlan.plan_uuid === "bravo-weekly-plan") {
          return true;
        } else if ((this.selectedPlan.period === "month" || this.selectedPlan.period === "monthly") && fetchedPlan.plan_uuid === "bravo-monthly-plan") {
          return true;
        } else if ((this.selectedPlan.period === "year" || this.selectedPlan.period === "yearly") && fetchedPlan.plan_uuid === "bravo-yearly-plan") {
          return true;
        }
        return false;
      });

      if (matchingPlan) {
        // Save plan details to localStorage
        localStorage.setItem('selectedPlan', JSON.stringify({
          id: matchingPlan.id,
          name: matchingPlan.name,
          plan_uuid: matchingPlan.plan_uuid,
          price: matchingPlan.price,
          period: this.selectedPlan.period
        }));
        
     
      } 
      await this.getRedirectUrl(matchingPlan.plan_uuid).then((response) => {
        if(response.data.success && response.data.paymentURL){
            window.location.href = response.data.paymentURL;
        }else{
            this.toast.error(response.data.message || "فشل في الحصول على رابط الدفع من برافو");
        }
      }).catch((error) => {
        console.error("Error getting redirect URL:", error);
        this.toast.error("فشل في الحصول على رابط الدفع من برافو");
      });

      // Navigate to Bravo payment page
      // this.$router.push({ name: 'bravo.pay' });
    }
  },

  async created() {
    // Load Bravo plans first
    await this.loadPlans();
   
    // Validate that we have the required parameters
    if (!this.country || !this.period) {
      this.toast.error("معاملات غير صحيحة");
      this.$router.push({ name: 'auth.subscriptions' });
      return;
    }

    // Validate that the plan exists
    if (!this.selectedPlan) {
      this.toast.error("الباقة المطلوبة غير متوفرة");
      this.$router.push({ name: 'auth.subscriptions' });
      return;
    }

   
  }
};
</script>

<style scoped>
/* Ensure proper height constraints for viewport */
.min-h-screen {
  height: 100vh;
  max-height: 100vh;
}

/* Custom scrollbar for the main content area */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #9d2890;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #b83bb1;
}

/* Operator card hover effects */
.form-group:hover {
  transform: translateY(-2px);
}

.form-group {
  transition: all 0.2s ease-in-out;
}
</style>
